import styled from 'styled-components'

const INK        = 'var(--vo-ink)'
const MUTED      = 'var(--vo-slate)'
const BLUE       = 'var(--color-brand)'
const BORDER     = 'var(--vo-cloud)'
const LABEL_COLOR = 'var(--vo-ink-2)'
const SUCCESS    = 'var(--vo-success)'
const ERROR      = 'var(--vo-danger)'
const WARNING    = 'var(--vo-warning)'

// ── Page wrapper ───────────────────────────────────────────────────────────────

export const PageWrap = styled.div`
  padding-top: 32px;
  padding-bottom: 40px;
`

export const FrameworkTag = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${LABEL_COLOR};
  margin: 0 0 20px;
`

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: 16px;
`

export const ResendBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 18px;
  background: var(--vo-heatmap-risky);
  color: ${WARNING};
  border: 1.5px solid color-mix(in srgb, var(--vo-warning) 40%, transparent);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: var(--vo-warning-15); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export const ReviewBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 18px;
  background: ${SUCCESS};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: var(--vo-emerald-600); }
`

export const ExecRequestBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 18px;
  background: var(--vo-heatmap-risky);
  color: ${WARNING};
  border: 1.5px solid color-mix(in srgb, var(--vo-warning) 40%, transparent);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: var(--vo-warning-15); }
`

// ── Status banners ─────────────────────────────────────────────────────────────

export const DraftBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: var(--vo-capri-50);
  border: 1px solid color-mix(in srgb, var(--vo-info) 20%, transparent);
  border-radius: 12px;
  margin-bottom: 16px;
`

export const PendingBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: var(--vo-heatmap-risky);
  border: 1px solid color-mix(in srgb, var(--vo-warning) 20%, transparent);
  border-radius: 12px;
  margin-bottom: 16px;
`

export const BannerInner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const BannerText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${({ $warning }) => $warning ? WARNING : BLUE};
  margin: 0;
  strong { color: inherit; }
`

// ── Main grid ──────────────────────────────────────────────────────────────────

export const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const FullWidth = styled.div`
  grid-column: 1 / -1;
`

// ── Cards ──────────────────────────────────────────────────────────────────────

export const DetailCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

export const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${BORDER};
`

// ── Field grid ────────────────────────────────────────────────────────────────

export const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

export const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`

export const FieldValueBold = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`

export const FieldValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${LABEL_COLOR};
  margin: 0;
`

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`

// ── Trust Score card ──────────────────────────────────────────────────────────

export const TrustCardInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
`

export const ScoreValue = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 48px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  letter-spacing: -0.03em;
  line-height: 1;
`

export const ScoreClass = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  margin: 0;
`

export const NoScoreText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-steel);
  font-style: italic;
  margin: 0;
  text-align: center;
`

export const ViewDetailsBtn = styled.button`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${BLUE};
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  margin-top: 4px;
  &:hover { text-decoration: underline; }
`

export const TrustLoadingBox = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`

// ── Executive approval card ────────────────────────────────────────────────────

export const ExecCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 4px;
`

export const ExecCardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0 0 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${BORDER};
`

export const ExecWarningBox = styled.div`
  background: var(--vo-heatmap-risky);
  border: 1px solid color-mix(in srgb, var(--vo-warning) 25%, transparent);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
`

export const ExecWarningText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${WARNING};
  margin: 0;
  strong { color: inherit; }
`

export const JustificationLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${LABEL_COLOR};
  display: block;
  margin-bottom: 6px;
`

export const JustificationHelper = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: ${MUTED};
  margin: 0 0 20px;
`

export const ApprovalActionsRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`

export const RejectBtn = styled.button`
  height: 44px;
  background: var(--vo-danger-10);
  color: ${ERROR};
  border: 1.5px solid color-mix(in srgb, var(--vo-danger) 30%, transparent);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover:not(:disabled) { background: var(--vo-danger-10); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export const ApproveBtn = styled.button`
  height: 44px;
  background: ${SUCCESS};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover:not(:disabled) { background: var(--vo-emerald-600); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

// ── Timeline ───────────────────────────────────────────────────────────────────

export const TimelineWrap = styled.div`
  position: relative;
  padding-left: 32px;
`

export const TimelineLine = styled.div`
  position: absolute;
  left: 12px;
  top: 4px;
  bottom: 4px;
  width: 2px;
  background: ${BORDER};
`

export const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 20px;
  &:last-child { padding-bottom: 0; }
`

export const TimelineDot = styled.div`
  position: absolute;
  left: -26px;
  top: 2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $color }) => $color || BLUE};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`

export const TimelineDate = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0 0 4px;
`

export const TimelineCard = styled.div`
  background: white;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  padding: 10px 14px;
`

export const TimelineTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 3px;
`

export const TimelineDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`

// ── Loading / Error ────────────────────────────────────────────────────────────

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
`

export const ErrorWrap = styled.div`
  padding-top: 32px;
`

export const ErrorBox = styled.div`
  background: var(--vo-danger-10);
  border: 1px solid var(--vo-danger-20);
  border-radius: 12px;
  padding: 14px 20px;
  margin-bottom: 16px;
`

export const ErrorText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${ERROR};
  margin: 0;
`

export const ErrorBackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  color: ${BLUE};
  padding: 4px 0;
  &:hover { text-decoration: underline; }
`
